import axios from 'axios';

const API = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:6555');

const api = axios.create({ baseURL: API, timeout: 30000 });

const redirectExpiredAdminSession = () => {
  if (!window.location.pathname.startsWith('/admin') || window.location.pathname === '/admin/login') return;
  localStorage.removeItem('admin_token');
  localStorage.removeItem('portal_token');
  window.location.replace('/admin/login');
};

api.interceptors.request.use(config => {
  const token = localStorage.getItem('admin_token') || localStorage.getItem('portal_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) redirectExpiredAdminSession();
    return Promise.reject(error);
  },
);

export const errorMessage = (error, fallback = 'Не удалось выполнить действие') =>
  error?.response?.data?.detail || error?.message || fallback;

export const getInventoryOptions = () => api.get('/api/inventory/options').then(response => response.data);
export const getInventoryOverview = params => api.get('/api/inventory/overview', { params }).then(response => response.data);

export const createItem = data => api.post('/api/inventory/items', data).then(response => response.data);
export const getItem = id => api.get(`/api/inventory/items/${id}`).then(response => response.data);
export const updateItem = (id, data) => api.patch(`/api/inventory/items/${id}`, data).then(response => response.data);
export const archiveItem = id => api.delete(`/api/inventory/items/${id}`).then(response => response.data);
export const restoreItem = id => api.post(`/api/inventory/items/${id}/restore`).then(response => response.data);

export const addEvent = (itemId, data) => api.post(`/api/inventory/items/${itemId}/events`, data).then(response => response.data);
export const deleteEvent = id => api.delete(`/api/inventory/log/${id}`).then(response => response.data);

export const createTask = (itemId, data) => api.post(`/api/inventory/items/${itemId}/tasks`, data).then(response => response.data);
export const updateTask = (id, data) => api.patch(`/api/inventory/tasks/${id}`, data).then(response => response.data);
export const deleteTask = id => api.delete(`/api/inventory/tasks/${id}`).then(response => response.data);

export const createRelation = (itemId, data) => api.post(`/api/inventory/items/${itemId}/relations`, data).then(response => response.data);
export const deleteRelation = id => api.delete(`/api/inventory/relations/${id}`).then(response => response.data);
export const linkTicket = (itemId, ticketId) => api.post(`/api/inventory/items/${itemId}/tickets`, { ticket_id: ticketId }).then(response => response.data);
export const unlinkTicket = id => api.delete(`/api/inventory/ticket-links/${id}`).then(response => response.data);

export const uploadAttachment = (itemId, file, eventId = null, onProgress = undefined) => {
  const body = new FormData();
  body.append('file', file);
  if (eventId) body.append('event_id', eventId);
  return api.post(`/api/inventory/items/${itemId}/attachments`, body, {
    timeout: 15 * 60 * 1000,
    onUploadProgress: onProgress,
  }).then(response => response.data);
};

export const getAttachmentBlob = attachmentId =>
  api.get(`/api/inventory/attachments/${attachmentId}/download`, { responseType: 'blob', timeout: 120000 })
    .then(response => response.data);
export const deleteAttachment = id => api.delete(`/api/inventory/attachments/${id}`).then(response => response.data);

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const exportInventory = async () => {
  const response = await api.get('/api/inventory/export', { responseType: 'blob', timeout: 120000 });
  saveBlob(response.data, `inventory-${new Date().toISOString().slice(0, 10)}.xlsx`);
};

export const importInventory = file => {
  const body = new FormData();
  body.append('file', file);
  return api.post('/api/inventory/import', body, { timeout: 120000 }).then(response => response.data);
};

export const getItemQrBlob = id =>
  api.get(`/api/inventory/items/${id}/qr`, { responseType: 'blob' }).then(response => response.data);
